export interface SvgPoint {
  x: number
  y: number
}

export interface SvgPathPoints {
  points: SvgPoint[]
  /** Whether the path has curve or arc commands — their on-path points are only endpoints. */
  curved: boolean
}

// Walks a path's `d` attribute and collects the points it visits. Curves and
// arcs contribute only their endpoints (the control points never lie on the
// path), so for those the result is the polyline joining their ends rather than
// the true outline — `curved` says when that approximation is in play.
export const parseSvgPathPoints = (d: string): SvgPathPoints | undefined => {
  const segments = d.match(/[A-Za-z][^A-Za-z]*/g)
  if (!segments) return undefined

  const points: SvgPoint[] = []
  let curved = false
  let x = 0
  let y = 0
  // A `z` jumps back to where the subpath began, and a relative command that
  // follows it moves from there.
  let startX = 0
  let startY = 0

  const visit = (nextX: number, nextY: number) => {
    x = nextX
    y = nextY
    points.push({ x, y })
  }

  // Every command but H/V/Z ends each of its coordinate groups on the
  // endpoint; `stride` is the group's length.
  const visitEndpoints = (numbers: number[], stride: number, relative: boolean) => {
    for (let i = 0; i + stride <= numbers.length; i += stride) {
      const endX = numbers[i + stride - 2]!
      const endY = numbers[i + stride - 1]!
      visit(relative ? x + endX : endX, relative ? y + endY : endY)
    }
  }

  for (const segment of segments) {
    const command = segment[0]!
    const numbers = segment.slice(1).trim().match(/-?\d*\.?\d+(?:e-?\d+)?/gi)?.map(Number) ?? []
    const relative = command === command.toLowerCase()

    switch (command.toUpperCase()) {
      case 'M':
        visitEndpoints(numbers.slice(0, 2), 2, relative)
        startX = x
        startY = y
        // Further pairs after a move are implicit line-tos.
        visitEndpoints(numbers.slice(2), 2, relative)
        break
      case 'T':
        curved = true
        visitEndpoints(numbers, 2, relative)
        break
      case 'L':
        visitEndpoints(numbers, 2, relative)
        break
      case 'H':
        for (const n of numbers) visit(relative ? x + n : n, y)
        break
      case 'V':
        for (const n of numbers) visit(x, relative ? y + n : n)
        break
      case 'C':
        curved = true
        visitEndpoints(numbers, 6, relative)
        break
      case 'S': case 'Q':
        curved = true
        visitEndpoints(numbers, 4, relative)
        break
      case 'A':
        curved = true
        visitEndpoints(numbers, 7, relative)
        break
      case 'Z':
        x = startX
        y = startY
        break
      default:
        return undefined
    }
  }

  return { points, curved }
}

// Every vertex of every basic shape in an SVG document, read straight off the
// markup — no DOM needed, so it runs on the server as well. Rounded rects and
// curved paths come back as their corner/end points, see parseSvgPathPoints.
export const parseSvgShapePoints = (svgContent: string): SvgPoint[] => {
  const points: SvgPoint[] = []
  const attr = (tag: string, name: string) => tag.match(new RegExp(`\\b${name}=["']([^"']*)["']`))?.[1]

  for (const [tag] of svgContent.matchAll(/<(?:rect|polygon|polyline|path)\b[^>]*>/g)) {
    if (tag.startsWith('<rect')) {
      const x = parseFloat(attr(tag, 'x') ?? '0')
      const y = parseFloat(attr(tag, 'y') ?? '0')
      const width = parseFloat(attr(tag, 'width') ?? '0')
      const height = parseFloat(attr(tag, 'height') ?? '0')
      if ([x, y, width, height].some(Number.isNaN)) continue
      points.push({ x, y }, { x: x + width, y }, { x: x + width, y: y + height }, { x, y: y + height })
    } else if (tag.startsWith('<path')) {
      const d = attr(tag, 'd')
      if (d) points.push(...(parseSvgPathPoints(d)?.points ?? []))
    } else {
      const numbers = attr(tag, 'points')?.trim().split(/[\s,]+/).map(Number) ?? []
      if (numbers.some(Number.isNaN)) continue
      for (let i = 0; i + 1 < numbers.length; i += 2) points.push({ x: numbers[i]!, y: numbers[i + 1]! })
    }
  }

  return points
}
